import {observer} from "mobx-react";
import {useContext, useRef, useState} from "react";
import {Button, Chip, Typography, alpha} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {Frame} from "../../common/Frame";
import {VideoPlayer} from "../../video/VideoPlayer";
import {LessonEditorStoreContext} from "../../../stores/LessonEditorStore";
import {lessons, recording} from "../../../proto/compiled";

interface AnnotationBlockEditorProps {
  block: lessons.IContentBlock;
}

const formatTimestamp = (ms?: number | null) => {
  if (ms === undefined || ms === null || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const AnnotationBlockEditor = observer(({block}: AnnotationBlockEditorProps) => {
  const store = useContext(LessonEditorStoreContext);
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);

  if (!store) return null;

  const videoExample = block.videoExample;
  const annotation = videoExample?.annotation;
  const hasAnnotation = annotation && annotation.id;

  const handleChangeAnnotation = () => {
    if (block.id) {
      store.openAnnotationSelector(block.id);
    }
  };

  const isGoodPractice = annotation?.annotationType === recording.AnnotationType.AT_GOOD_PRACTICE;
  const isCommonMistake = annotation?.annotationType === recording.AnnotationType.AT_COMMON_MISTAKE;

  const badgeColor = isGoodPractice
    ? theme.palette.success.main
    : isCommonMistake
      ? theme.palette.error.main
      : theme.palette.grey[600];

  const badgeText = isGoodPractice
    ? "GOOD PRACTICE"
    : isCommonMistake
      ? "COMMON MISTAKE"
      : "VIDEO EXAMPLE";

  const BadgeIcon = isGoodPractice ? CheckIcon : isCommonMistake ? CloseIcon : null;

  if (!hasAnnotation) {
    return (
      <Frame
        padding={3}
        radius={1}
        center
        clickable
        onClick={handleChangeAnnotation}
        style={{
          border: `2px dashed ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Frame direction="column" center gap={1}>
          <VideoLibraryIcon color="disabled" fontSize="large"/>
          <Typography variant="body2" color="textSecondary">
            Click to select a video example
          </Typography>
        </Frame>
      </Frame>
    );
  }

  return (
    <Frame
      row
      gap={2}
      padding={2}
      radius={1}
      style={{
        border: `2px solid ${badgeColor}`,
        backgroundColor: alpha(badgeColor, 0.04),
      }}
    >
      {/* Video Player */}
      {videoExample?.videoUrl && annotation && (
        <Frame
          direction="column"
          style={{
            width: '50%',
            flexShrink: 0,
          }}
        >
          <VideoPlayer
            videoRef={videoRef}
            src={videoExample.videoUrl}
            annotations={[annotation as recording.IRecordingAnnotation]}
            selectedAnnotation={annotation as recording.IRecordingAnnotation}
            limitToAnnotation={annotation as recording.IRecordingAnnotation}
            initialTimeMs={annotation.startMs ?? 0}
            sizing="contain"
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
          />
        </Frame>
      )}

      {/* Content */}
      <Frame direction="column" gap={1.5} grow>
        {/* Header with type badge */}
        <Frame row justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Frame direction="column" gap={0.5} grow>
            <Frame row gap={0.5} alignItems="center">
              {BadgeIcon && (
                <BadgeIcon fontSize="small" style={{color: badgeColor}}/>
              )}
              <Typography
                variant="caption"
                fontWeight="bold"
                style={{color: badgeColor, letterSpacing: '0.5px'}}
              >
                {badgeText}
              </Typography>
            </Frame>
            <Typography variant="subtitle1" fontWeight={600}>
              {annotation?.label || "Untitled"}
            </Typography>
          </Frame>

          <Button
            variant="outlined"
            size="small"
            startIcon={<SwapHorizIcon/>}
            onClick={handleChangeAnnotation}
          >
            Change
          </Button>
        </Frame>

        {/* Description */}
        {annotation?.description && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {annotation.description}
          </Typography>
        )}

        {/* Time range and recording info */}
        <Frame row gap={2} alignItems="center" style={{marginTop: 'auto'}}>
          <Chip
            label={`${formatTimestamp(annotation?.startMs)}${annotation?.endMs ? ` → ${formatTimestamp(annotation?.endMs)}` : ''}`}
            size="small"
            variant="outlined"
          />
          {videoExample?.recordingName && (
            <Typography variant="caption" color="textSecondary">
              from: {videoExample.recordingName}
            </Typography>
          )}
        </Frame>
      </Frame>
    </Frame>
  );
});
